<script>
import List from "@/components/molecules/list";
import ButtonIcon from "@/components/molecules/button-icon";
import TitleImage from "@/components/molecules/title-image";
import Brands from "@/components/organisms/header/menu/brands";
import ListItem from "@/components/molecules/list/list-item";
import {
  headerMenuLayerEmits,
  headerMenuLayerProps,
  useHeaderMenuLayer
} from "@/components/organisms/header/menu/menu-layer/mixin";
import HtmlParser from "@/components/organisms/cms-container/html-parser";
import LazyImage from "@/components/atoms/lazy-image";
import PopinAnchor from "@/components/molecules/popin-anchor";
import MiamMenuLayer from "@/components/organisms/header/menu/menu-layer/miam";
import { focusOnElement } from "~/utils/focus";

import pictoAlcoolWeb from "@/assets/images/picto-alcool-web.svg";

export default {
  name: "MenuLayer",
  expose: ["reset"],
  components: {
    TitleImage,
    Brands,
    ListItem,
    HtmlParser,
    LazyImage,
    PopinAnchor,
    MiamMenuLayer,
    List,
    ButtonIcon
  },
  props: {
    miamPushZone: {
      type: Array,
      required: false,
      default: () => []
    },
    ...headerMenuLayerProps()
  },
  emits: ["categorySelected", "close", ...headerMenuLayerEmits()],
  setup(props, context) {
    const {
      categoryRouting,
      currentCategory,
      parentCategory,
      categoryRef,
      reset,
      redirectedToProductList,
      categoryAccessed,
      getPushData,
      menuCodes
    } = useHeaderMenuLayer(props, context);

    const categoryLibelle = ref(null);

    return {
      categoryRouting,
      currentCategory,
      parentCategory,
      categoryRef,
      reset,
      redirectedToProductList,
      categoryAccessed,
      getPushData,
      menuCodes,
      pictoAlcoolWeb,
      categoryLibelle
    };
  },
  methods: {
    select(category) {
      this.categoryRouting.push(category);
      this.$nextTick(() => {
        focusOnElement(this.$refs.menuLink[0].$el);
      });
      this.$emit("categorySelected");
    },
    goToPrevious() {
      if (this.categoryRouting.length > 1) {
        this.categoryRouting.pop();
        this.$nextTick(() => {
          focusOnElement(this.$refs.menuLink[0].$el);
        });
      } else {
        this.$emit("close");
      }
    }
  },
  computed: {
    isMiam() {
      return this.currentCategory.attributes.isMiam;
    },
    layerCategoriesFiltered() {
      return this.currentCategory.attributes.children.data.filter(
        (child) => !child.attributes.hidden_on_desktop
      );
    }
  }
};
</script>

<template>
  <div class="c-header-menu-layer">
    <div class="c-header-menu-layer__container">
      <div class="c-header-menu-layer__heading">
        <ButtonIcon
          v-if="parentCategory"
          class="c-header-menu-layer__btn-return"
          icon-name="arrow-left"
          icon-color="text-light"
          icon-color-hover="primary-light"
          icon-size="xs"
          size="sm"
          @click="goToPrevious()"
        >
          Retour à {{ parentCategory.attributes.libelle }}
        </ButtonIcon>
        <div class="u-flex u-flex-justify-center">
          <div
            class="u-flex u-flex-align-center"
            :class="currentCategory.attributes.alcool && 'c-header-menu-layer__alcool'"
          >
            <p
              v-uid
              ref="categoryLibelle"
              class="c-header-menu-layer__title"
            >
              {{ currentCategory.attributes.libelle }}
            </p>
            <div
              v-if="currentCategory.attributes.alcool"
              :class="currentCategory.attributes.alcool &&'c-header-menu-layer__alcool-img u-ml-md'"
            >
              <PopinAnchor
                position="bottom"
                popin-size="xs"
                open-on-hover
                radius="md"
                aria-label="La loi interdit la vente d’alcool aux mineurs"
              >
                <template #button-content>
                  <LazyImage
                    :src="pictoAlcoolWeb"
                    width="88"
                    height="39"
                  />
                </template>
                <p class="u-font-primary u-font-14 u-font-400 u-color-text">
                  La loi interdit la vente d’alcool aux mineurs
                </p>
              </PopinAnchor>
            </div>
          </div>
        </div>
      </div>
      <div class="c-header-menu-layer__categories-wrapper">
        <template v-if="isMiam">
          <MiamMenuLayer :push-zones="miamPushZone" />
        </template>
        <template v-else>
          <List
            :class="{'c-header-menu-layer__categories--with-push-zone':currentCategory.attributes.push_zones.length > 0}"
            class="c-header-menu-layer__categories"
            :aria-labelledby="categoryLibelle?.id"
          >
            <ListItem
              v-for="child in layerCategoriesFiltered"
              :key="child.id"
              class="c-header-menu-layer__category"
            >
              <template v-if="!child.attributes.children || child.attributes.children.data.length === 0 || showLinks || child.attributes.web_behaviour === 'SkipChildren'">
                <TitleImage
                  ref="menuLink"
                  :image-path="child.attributes.visuel"
                  class="c-header-menu-layer__title-image"
                  :slug="child.attributes.slug"
                  :dimension="200"
                  @click="redirectedToProductList(child)"
                >
                  {{ child.attributes.libelle }}
                </TitleImage>
                <span
                  v-if="child.attributes.colors"
                  :style="child.attributes.colors.background && {backgroundColor: `${child.attributes.colors.background}`}"
                  class="c-header-menu-layer__nav-button-line "
                ></span>
              </template>
              <template v-else>
                <TitleImage
                  ref="menuLink"
                  :image-path="child.attributes.visuel"
                  class="c-header-menu-layer__title-image"
                  :dimension="200"
                  @click="select(child)"
                >
                  {{ child.attributes.libelle }}
                </TitleImage>
                <span
                  v-if="child.attributes.colors"
                  :style="child.attributes.colors.background && {backgroundColor: `${child.attributes.colors.background}`}"
                  class="c-header-menu-layer__nav-button-line "
                ></span>
              </template>
            </ListItem>
          </List>
        </template>
        <div
          v-if="getPushData(currentCategory, menuCodes.MEA_1).html_content || getPushData(currentCategory, menuCodes.MEA_2).html_content || getPushData(currentCategory, menuCodes.MEA_3).html_content"
          class="c-header-menu-layer__zone-merch"
        >
          <HtmlParser
            v-if="getPushData(currentCategory, menuCodes.MEA_1).html_content"
            context="Menu MEA1"
            :promo-printing="true"
            :push-data="getPushData(currentCategory, menuCodes.MEA_1)"
            class="u-mb-sm u-border-grey-4"
          />
          <HtmlParser
            v-if="getPushData(currentCategory, menuCodes.MEA_2).html_content"
            context="Menu MEA2"
            :promo-printing="true"
            :push-data=" getPushData(currentCategory, menuCodes.MEA_2)"
            class="u-mb-sm u-border-grey-4"
          />
          <HtmlParser
            v-if="getPushData(currentCategory, menuCodes.MEA_3).html_content && currentCategory.attributes.children.data.length > menuCodes.CATEGORIES_PER_LINE "
            context="Menu MEA2"
            :promo-printing="true"
            :push-data=" getPushData(currentCategory, menuCodes.MEA_3)"
            class="u-mb-sm u-border-grey-4"
          />
        </div>
      </div>
      <Brands
        v-if="getPushData(currentCategory,menuCodes.BRANDS).html_content"
        :push-data="getPushData(currentCategory, menuCodes.BRANDS)"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/components/header-menu-layer.scss";
</style>
